import { useState, useEffect, useCallback } from 'react';
import { fetchAuditLogs, fetchAllAuditLogs, fetchUsers } from '../services/authService';
import { exportTableAsCSV, generateExportFilename } from '../utils/exportUtils';
import {
  Shield, Search, RefreshCw, ChevronLeft, ChevronRight,
  LogIn, LogOut, UserPlus, UserCog, Key, CalendarDays, X,
  Download, FileText, Users, CheckCircle2, AlertTriangle,
  Eye, Mail, Send, Archive, Copy, RotateCcw, Trash2, Pause, Activity, ClipboardList, FilePlus,
} from 'lucide-react';

// ─── Action badge config ──────────────────────────────────────────────────────
// Every action the backend writes needs an entry here. An action that is missing
// still displays — it falls back to its raw database name — but it lands in no
// category, so no filter chip can ever reach it. That is how a third of the
// audit trail became unreachable: the clinical and campaign events were being
// recorded correctly and had simply never been registered.
//
// Colour carries meaning: green/blue for reading, amber/orange for changing,
// red for destructive. It is the quickest way to scan a page for changes.
const ACTION_CONFIG = {
  // ─── Authentication ──────────────────────────────────────────────────────
  LOGIN_SUCCESS:             { label: 'Login',               color: 'bg-emerald-100 text-emerald-700', icon: LogIn,         category: 'auth'     },
  LOGIN_FAILED:              { label: 'Login Failed',        color: 'bg-red-100 text-red-700',         icon: AlertTriangle, category: 'auth'     },
  LOGOUT:                    { label: 'Logout',              color: 'bg-gray-100 text-gray-600',       icon: LogOut,        category: 'auth'     },
  PASSWORD_RESET_SUCCESS:    { label: 'Password Reset',      color: 'bg-purple-100 text-purple-700',   icon: Key,           category: 'auth'     },
  PASSWORD_CHANGED:          { label: 'Password Changed',    color: 'bg-purple-100 text-purple-700',   icon: Key,           category: 'auth'     },
  FORGOT_PASSWORD_REQUESTED: { label: 'Forgot Password',     color: 'bg-indigo-100 text-indigo-700',   icon: Key,           category: 'auth'     },

  // ─── User management ─────────────────────────────────────────────────────
  USER_CREATED:              { label: 'User Created',        color: 'bg-blue-100 text-blue-700',       icon: UserPlus,      category: 'users'    },
  USER_UPDATED:              { label: 'User Updated',        color: 'bg-amber-100 text-amber-700',     icon: UserCog,       category: 'users'    },
  USER_DEACTIVATED:          { label: 'Deactivated',         color: 'bg-orange-100 text-orange-700',   icon: UserCog,       category: 'users'    },
  USER_ASSIGNMENTS_UPDATED:  { label: 'Assignments Updated', color: 'bg-teal-100 text-teal-700',       icon: Users,         category: 'users'    },
  USER_ACCESS_LINK_RESENT:   { label: 'Access Link Resent',  color: 'bg-sky-100 text-sky-700',         icon: Send,          category: 'users'    },

  // ─── Clinical (CAR-T Playbook) ───────────────────────────────────────────
  // The records an audit actually asks about — who opened or changed a
  // patient's case, and when.
  PLAYBOOK_PATIENT_VIEWED:   { label: 'Patient Viewed',      color: 'bg-blue-100 text-blue-700',       icon: Eye,           category: 'clinical' },
  PLAYBOOK_PATIENTS_VIEWED:  { label: 'Patient List Viewed', color: 'bg-blue-50 text-blue-600',        icon: ClipboardList, category: 'clinical' },
  PLAYBOOK_PATIENT_CREATED:  { label: 'Patient Created',     color: 'bg-emerald-100 text-emerald-700', icon: UserPlus,      category: 'clinical' },
  PLAYBOOK_PATIENT_UPDATED:  { label: 'Patient Updated',     color: 'bg-amber-100 text-amber-700',     icon: Activity,      category: 'clinical' },
  PLAYBOOK_CASE_VIEWED:      { label: 'Case Viewed',         color: 'bg-blue-100 text-blue-700',       icon: Eye,           category: 'clinical' },
  PLAYBOOK_CASES_VIEWED:     { label: 'Case List Viewed',    color: 'bg-blue-50 text-blue-600',        icon: ClipboardList, category: 'clinical' },
  PLAYBOOK_CASE_CREATED:     { label: 'Case Created',        color: 'bg-emerald-100 text-emerald-700', icon: FilePlus,       category: 'clinical' },
  PLAYBOOK_CASE_UPDATED:     { label: 'Case Updated',        color: 'bg-amber-100 text-amber-700',     icon: Activity,      category: 'clinical' },
  PLAYBOOK_CASE_COMPLETED:   { label: 'Case Completed',      color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2,  category: 'clinical' },
  PLAYBOOK_CASE_REOPENED:    { label: 'Case Reopened',       color: 'bg-sky-100 text-sky-700',         icon: RotateCcw,     category: 'clinical' },
  PLAYBOOK_CASE_DERIVED:     { label: 'Case Derived',        color: 'bg-violet-100 text-violet-700',   icon: Copy,          category: 'clinical' },
  PLAYBOOK_CASE_ARCHIVED:    { label: 'Case Archived',       color: 'bg-orange-100 text-orange-700',   icon: Archive,       category: 'clinical' },
  PLAYBOOK_CASE_RESTORED:    { label: 'Case Restored',       color: 'bg-teal-100 text-teal-700',       icon: RotateCcw,     category: 'clinical' },
  PLAYBOOK_CASE_DELETED:     { label: 'Case Deleted',        color: 'bg-red-100 text-red-700',         icon: Trash2,        category: 'clinical' },
  PLAYBOOK_CASE_EXPORTED:    { label: 'Case PDF Exported',   color: 'bg-indigo-100 text-indigo-700',   icon: Download,      category: 'clinical' },

  // ─── Email campaigns ─────────────────────────────────────────────────────
  CAMPAIGN_CREATED:          { label: 'Campaign Created',    color: 'bg-blue-100 text-blue-700',       icon: Mail,          category: 'email'    },
  CAMPAIGN_UPDATED:          { label: 'Campaign Updated',    color: 'bg-amber-100 text-amber-700',     icon: Mail,          category: 'email'    },
  CAMPAIGN_DELETED:          { label: 'Campaign Deleted',    color: 'bg-red-100 text-red-700',         icon: Trash2,        category: 'email'    },
  CAMPAIGN_RECIPIENTS_ADDED: { label: 'Recipients Added',    color: 'bg-cyan-100 text-cyan-700',       icon: Users,         category: 'email'    },
  CAMPAIGN_TEST_SENT:        { label: 'Test Email Sent',     color: 'bg-indigo-100 text-indigo-700',   icon: Send,          category: 'email'    },
  CAMPAIGN_CONFIRMED:        { label: 'Campaign Confirmed',  color: 'bg-yellow-100 text-yellow-700',   icon: CheckCircle2,  category: 'email'    },
  CAMPAIGN_SEND_STARTED:     { label: 'Send Started',        color: 'bg-emerald-100 text-emerald-700', icon: Send,          category: 'email'    },
  CAMPAIGN_PAUSED:           { label: 'Campaign Paused',     color: 'bg-orange-100 text-orange-700',   icon: Pause,         category: 'email'    },
  CAMPAIGN_RETRY_FAILED:     { label: 'Failed Retried',      color: 'bg-purple-100 text-purple-700',   icon: RotateCcw,     category: 'email'    },
  EMAIL_OUTBOX_RETRIED:      { label: 'Email Retried',       color: 'bg-purple-100 text-purple-700',   icon: RotateCcw,     category: 'email'    },

  // ─── System ──────────────────────────────────────────────────────────────
  TERMS_ACCEPTED:            { label: 'Terms Accepted',      color: 'bg-green-100 text-green-700',     icon: CheckCircle2,  category: 'system'   },
  CSV_SYNC_IMPORT:           { label: 'CSV Sync',            color: 'bg-cyan-100 text-cyan-700',       icon: FileText,      category: 'system'   },
};

// Actions hidden from UI (system noise).
// SESSION_PING is the engagement report's heartbeat — one row per session,
// refreshed constantly. Showing it would drown out everything else.
const HIDDEN_ACTIONS = ['TOKEN_REFRESHED', 'REFRESH_TOKEN_INVALID', 'SESSION_PING'];

// Filter chip categories
const FILTER_CHIPS = [
  { id: 'all',      label: 'All',             tooltip: 'All events' },
  { id: 'auth',     label: 'Authentication',  tooltip: 'Login, Logout, Login Failed, Password Reset, Password Changed, Forgot Password' },
  { id: 'users',    label: 'User Management', tooltip: 'User Created, Updated, Deactivated, Assignments, Access Link Resent' },
  { id: 'clinical', label: 'Clinical',        tooltip: 'Patient and case records viewed, created, updated, completed, reopened, exported, archived or restored' },
  { id: 'email',    label: 'Email',           tooltip: 'Campaigns created, confirmed, sent, paused, and email retries' },
  { id: 'system',   label: 'System',          tooltip: 'Terms Accepted, CSV Sync' },
];

/**
 * Which actions belong to a chip, worked out from ACTION_CONFIG rather than a
 * second hand-written list. The two lists used to be maintained separately,
 * which is how they drifted apart — a new action could be given a label and
 * still belong to no chip. Deriving it means registering an action above is
 * now the only step required.
 */
const CATEGORY_ACTIONS = Object.entries(ACTION_CONFIG).reduce((acc, [action, cfg]) => {
  if (HIDDEN_ACTIONS.includes(action)) return acc;
  (acc[cfg.category] = acc[cfg.category] || []).push(action);
  return acc;
}, {});

function getActionConfig(action) {
  return ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-600', icon: Shield, category: 'other' };
}

// Relative time helper
function getRelativeTime(dateStr) {
  const normalized = dateStr.replace(' ', 'T').replace(/(\.\d+)?Z?$/, '') + '+05:30';
  const d = new Date(normalized);
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return null; // Show absolute time only
}

export default function AuditLogs() {
  const [logs,           setLogs]           = useState([]);
  const [pagination,     setPagination]     = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [exportingAll,   setExportingAll]   = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [search,         setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userFilter,     setUserFilter]     = useState('');
  const [userSearch,     setUserSearch]     = useState(''); // For searchable user dropdown
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [users,          setUsers]          = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [sortBy,         setSortBy]         = useState('created_at');
  const [sortDir,        setSortDir]        = useState('desc');

  // Debounce search — wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Map category to backend action names
  // Derived from ACTION_CONFIG, so a newly registered action is filterable
  // immediately rather than needing to be listed in a second place.
  const getCategoryActions = (category) => (CATEGORY_ACTIONS[category] || []).join(',');

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAuditLogs({
        page,
        limit: pagination.limit,
        search: debouncedSearch,
        user_id: userFilter,
        date_from: dateFrom,
        date_to: dateTo,
        exclude_actions: HIDDEN_ACTIONS.join(','),
        include_actions: categoryFilter !== 'all' ? getCategoryActions(categoryFilter) : '',
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, userFilter, dateFrom, dateTo, pagination.limit, categoryFilter, sortBy, sortDir]);

  useEffect(() => { loadLogs(1); }, [debouncedSearch, userFilter, dateFrom, dateTo, categoryFilter, sortBy, sortDir]); // eslint-disable-line

  // Load users list for filter dropdown
  useEffect(() => {
    fetchUsers({ limit: 200 }).then(data => setUsers(data.users || [])).catch(() => {});
  }, []);

  // Close user dropdown on click outside
  useEffect(() => {
    if (!showUserDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('[data-user-dropdown]')) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserDropdown]);

  // Logs are already filtered by backend — just use directly
  const filteredLogs = logs;

  // Sort handler — click header to cycle: asc → desc → reset
  const handleSort = (column) => {
    if (sortBy === column) {
      if (sortDir === 'desc') {
        setSortDir('asc');
      } else {
        // Reset to default
        setSortBy('created_at');
        setSortDir('desc');
      }
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  // Sort indicator arrow
  const SortArrow = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-emerald-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const formatDate = (dateStr) => {
    const normalized = dateStr.replace(' ', 'T').replace(/(\.\d+)?Z?$/, '') + '+05:30';
    const d = new Date(normalized);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    }) + ' ' + d.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true,
    }).toUpperCase();
  };

  // Get display label for active category
  const categoryLabel = categoryFilter === 'all' ? 'Complete' : (FILTER_CHIPS.find(c => c.id === categoryFilter)?.label || 'All');

  // Export current page logs as CSV
  const handleExportCSV = () => {
    const headers = ['Action', 'User', 'Email', 'Role', 'Date & Time (IST)', 'Session Duration', 'Attempted Email'];
    const rows = filteredLogs.map(log => [
      getActionConfig(log.action).label,
      log.user_name || 'System',
      log.user_email || '',
      log.user_role || '',
      formatDate(log.created_at),
      log.action === 'LOGOUT' && log.detail?.session_duration_minutes != null
        ? `${log.detail.session_duration_minutes} min${log.detail.session_type === 'idle_timeout' ? ' (timed out)' : ''}`
        : '',
      log.action === 'LOGIN_FAILED' ? (log.detail?.email || '') : '',
    ]);
    const filename = generateExportFilename(`Audit_Logs_${categoryLabel}_Page${pagination.page}`);
    exportTableAsCSV(headers, rows, filename);
  };

  // Export ALL logs as CSV — walks every page rather than capping at 10,000
  const handleExportAllCSV = async () => {
    setExportingAll(true);
    setExportProgress('');
    setError('');
    try {
      const { logs: allLogs } = await fetchAllAuditLogs(
        {
          search: debouncedSearch,
          user_id: userFilter,
          date_from: dateFrom,
          date_to: dateTo,
          exclude_actions: HIDDEN_ACTIONS.join(','),
          include_actions: categoryFilter !== 'all' ? getCategoryActions(categoryFilter) : '',
          sort_by: sortBy,
          sort_dir: sortDir,
        },
        (loaded, total) => setExportProgress(`${loaded} of ${total}`)
      );

      // Attempted address is included so a run of failed logins shows which
      // account was being targeted — the point of reviewing them at all.
      const headers = ['Action', 'User', 'Email', 'Role', 'Date & Time (IST)', 'Session Duration', 'Attempted Email'];
      const rows = allLogs.map(log => [
        getActionConfig(log.action).label,
        log.user_name || 'System',
        log.user_email || '',
        log.user_role || '',
        formatDate(log.created_at),
        log.action === 'LOGOUT' && log.detail?.session_duration_minutes != null
          ? `${log.detail.session_duration_minutes} min${log.detail.session_type === 'idle_timeout' ? ' (timed out)' : ''}`
          : '',
        log.action === 'LOGIN_FAILED' ? (log.detail?.email || '') : '',
      ]);
      const filename = generateExportFilename(`Audit_Logs_${categoryLabel}_All`);
      exportTableAsCSV(headers, rows, filename);
    } catch (err) {
      setError('Failed to export all logs: ' + (err.message || ''));
    } finally {
      setExportingAll(false);
      setExportProgress('');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} total events</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={filteredLogs.length === 0}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title="Export current page as CSV"
          >
            <Download className="w-4 h-4" />
            <span>Export Page</span>
          </button>
          <button
            onClick={handleExportAllCSV}
            disabled={pagination.total === 0 || exportingAll}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title={`Exports all ${pagination.total} entries matching the current filters`}
          >
            <Download className={`w-4 h-4 ${exportingAll ? 'animate-pulse' : ''}`} />
            <span>
              {exportingAll
                ? `Exporting${exportProgress ? ` ${exportProgress}` : ''}...`
                : `Export All (${pagination.total})`}
            </span>
          </button>
          <button
            onClick={() => loadLogs(pagination.page)}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Category Filter Chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTER_CHIPS.map(chip => (
          <button
            key={chip.id}
            onClick={() => setCategoryFilter(chip.id)}
            title={chip.tooltip}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              categoryFilter === chip.id
                ? 'bg-emerald-50 border-emerald-400 text-emerald-700 shadow-sm'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Global Search + Searchable User Filter + Date Range */}
      <div className="flex flex-col items-center sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-5 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by action (login, logout, password...)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Searchable User Dropdown */}
        <div className="relative w-56" data-user-dropdown>
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500">
            <input
              type="text"
              placeholder="Filter by user..."
              value={userSearch}
              onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
              onFocus={() => setShowUserDropdown(true)}
              className="flex-1 px-3 py-2 text-sm focus:outline-none border-none"
            />
            {userFilter && (
              <button
                onClick={() => { setUserFilter(''); setUserSearch(''); }}
                className="px-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {showUserDropdown && (
            <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              <button
                onClick={() => { setUserFilter(''); setUserSearch(''); setShowUserDropdown(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${!userFilter ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}
              >
                All Users
              </button>
              {users
                .filter(u => {
                  if (!userSearch) return true;
                  const q = userSearch.toLowerCase();
                  return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
                })
                .slice(0, 20) // Show max 20 results
                .map(u => (
                  <button
                    key={u.id}
                    onClick={() => { setUserFilter(u.id); setUserSearch(u.name); setShowUserDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${String(userFilter) === String(u.id) ? 'bg-emerald-50' : ''}`}
                  >
                    <span className="truncate">{u.name}</span>
                    <span className="text-[10px] text-gray-400 capitalize ml-2 flex-shrink-0">{u.role}</span>
                  </button>
                ))
              }
              {users.filter(u => {
                if (!userSearch) return true;
                const q = userSearch.toLowerCase();
                return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
              }).length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400">No users found</div>
              )}
            </div>
          )}
        </div>

        {/* Date range pickers */}
        <div className="flex items-center gap-1.5 border border-gray-300 rounded-lg bg-white px-3 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 transition-all">
          <CalendarDays className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex items-center gap-1.5">
            <div className="flex flex-col">
              <span className="text-[10px] font-medium text-gray-400 leading-none mb-0.5">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="text-sm text-gray-700 bg-transparent border-none outline-none w-[118px] cursor-pointer"
                title="From date"
              />
            </div>
            <span className="text-gray-300 font-light text-base select-none">|</span>
            <div className="flex flex-col">
              <span className="text-[10px] font-medium text-gray-400 leading-none mb-0.5">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="text-sm text-gray-700 bg-transparent border-none outline-none w-[118px] cursor-pointer"
                title="To date"
              />
            </div>
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear dates"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Quick date presets */}
        <div className="flex items-center gap-1.5">
          {[
            { label: 'Today',   days: 0  },
            { label: '7d',      days: 7  },
            { label: '30d',     days: 30 },
          ].map(({ label, days }) => {
            const today = new Date();
            const toStr = today.toISOString().slice(0, 10);
            const fromStr = days === 0
              ? toStr
              : new Date(today.setDate(today.getDate() - days)).toISOString().slice(0, 10);
            const isActive = dateFrom === fromStr && dateTo === toStr;
            return (
              <button
                key={label}
                onClick={() => { setDateFrom(fromStr); setDateTo(toStr); }}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {(search || userFilter || dateFrom || dateTo || categoryFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setUserFilter(''); setUserSearch(''); setDateFrom(''); setDateTo(''); setCategoryFilter('all'); }}
            className="flex h-9 items-center gap-1 px-2.5 py-1 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear all
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th onClick={() => handleSort('action')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '15%' }}>
                  Action <SortArrow column="action" />
                </th>
                <th onClick={() => handleSort('user_name')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '22%' }}>
                  User <SortArrow column="user_name" />
                </th>
                <th onClick={() => handleSort('user_role')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '12%' }}>
                  Role <SortArrow column="user_role" />
                </th>
                <th onClick={() => handleSort('created_at')} className="text-center px-4 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors" style={{ width: '30%' }}>
                  Date & Time (IST) <SortArrow column="created_at" />
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600" style={{ width: '21%' }}>
                  Session Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400">No audit logs found</td>
                </tr>
              ) : filteredLogs.map(log => {
                const cfg  = getActionConfig(log.action);
                const Icon = cfg.icon;
                const isFailed = log.action === 'LOGIN_FAILED';
                const relative = getRelativeTime(log.created_at);

                return (
                  <tr key={log.id} className="group hover:bg-gray-50 transition-colors">
                    <td className={`px-4 py-3 text-center ${isFailed ? 'border-l-3 border-l-red-400' : ''}`}>
                      <div className="flex flex-col items-center gap-1.5">
                        <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                          <Icon className="w-3 h-3" />
                          <span>{cfg.label}</span>
                        </span>
                        {(log.action === 'LOGIN_SUCCESS' || log.action === 'LOGOUT') && log.detail?.session_id && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500">
                            {log.detail.session_id.slice(-6)}
                          </span>
                        )}
                        {/* Case + export reference (printed in full on the PDF). */}
                        {log.action === 'PLAYBOOK_CASE_EXPORTED' && log.detail?.exportRef && (
                          <span
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500"
                            title={`Export reference ${log.detail.exportRef}`}
                          >
                            Case #{log.detail.caseId} · {log.detail.exportRef.slice(-6)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.user_name ? (
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{log.user_name}</p>
                          <p className="text-xs text-gray-500 truncate">{log.user_email}</p>
                        </div>
                      ) : log.action === 'LOGIN_FAILED' && log.detail?.email ? (
                        /* A failed login has no user_id — the account did not
                           authenticate — so it would otherwise read "System".
                           The address that was tried is the one thing worth
                           seeing here: a run of them against one account is
                           what a brute-force attempt looks like. */
                        <div>
                          <p className="text-xs text-gray-400 italic">attempted</p>
                          <p className="text-sm font-medium text-red-700 truncate">{log.detail.email}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs italic">System / Unknown</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.user_role ? (
                        <span className="text-[14px] font-medium text-gray-600 capitalize">{log.user_role}</span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-700">{formatDate(log.created_at)}</span>
                        {relative && (
                          <span className="text-xs text-gray-400 mt-0.5">{relative}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.action === 'LOGOUT' && log.detail?.session_duration_minutes != null ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.detail.session_type === 'idle_timeout'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {(() => {
                            const mins = log.detail.session_duration_minutes;
                            if (mins < 60) return `${mins}m`;
                            const h = Math.floor(mins / 60);
                            const m = mins % 60;
                            return m > 0 ? `${h}h ${m}m` : `${h}h`;
                          })()}
                          {log.detail.session_type === 'idle_timeout' && ' (timed out)'}
                        </span>
                      ) : log.action === 'LOGIN_SUCCESS' && (() => {
                        // Show "Active" only if this login is recent (within 35 min) and has no matching logout
                        const loginTime = new Date(log.created_at.replace(' ', 'T').replace(/(\.\d+)?Z?$/, '') + '+05:30');
                        const minutesAgo = (Date.now() - loginTime) / 60000;
                        if (minutesAgo > 31) return false; // Too old — session would have timed out

                        const logSessionId = log.detail?.session_id;
                        if (logSessionId) {
                          const hasMatchingLogout = filteredLogs.some(
                            l => l.action === 'LOGOUT' && l.detail?.session_id === logSessionId
                          );
                          return !hasMatchingLogout;
                        }
                        // Fallback for old logs without session_id
                        const logIndex = filteredLogs.indexOf(log);
                        const hasLogoutAfter = filteredLogs.slice(0, logIndex).some(
                          l => l.action === 'LOGOUT' && l.user_name === log.user_name
                        );
                        return !hasLogoutAfter && logIndex === filteredLogs.findIndex(
                          l => l.action === 'LOGIN_SUCCESS' && l.user_name === log.user_name
                        );
                      })() ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                          Active
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} events)
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadLogs(pagination.page - 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadLogs(pagination.page + 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {pagination.totalPages <= 1 && pagination.total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">Showing {pagination.total} events</span>
          </div>
        )}
      </div>
    </div>
  );
}
